import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  IonAlert,
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonListHeader,
  IonModal,
  IonNote,
  IonPage,
  IonProgressBar,
  IonRadio,
  IonRadioGroup,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonText,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToggle,
  IonToolbar,
} from "@ionic/react";
import {
  addOutline,
  cloudUploadOutline,
  createOutline,
  eyeOutline,
  linkOutline,
  trashOutline,
} from "ionicons/icons";

import Header from "src/components/Header";
import {
  CargoOrganizacion,
  CategoriaRecurso,
  Recurso,
  RecursoEstado,
  RecursoPayload,
  RecursoProveedor,
  RecursoTipo,
  VistaRecurso,
  adminActualizarRecurso,
  adminCambiarEstado,
  adminCrearRecurso,
  adminEliminarRecurso,
  adminListarCargos,
  adminListarRecursos,
  adminVistasRecurso,
  esAdminRecursos,
  formatearDuracion,
  listarCategorias,
  subirVideo,
} from "src/services/recursos";

import "./Recursos.css";

interface Toast {
  show: boolean;
  msg: string;
  color?: string;
}

type ModoOrigen = "SUBIR" | "ENLACE";

interface Formulario {
  id?: number;
  categoria_clave: string;
  titulo: string;
  descripcion: string;
  tipo: RecursoTipo;
  modoOrigen: ModoOrigen;
  public_id: string;
  url_externa: string;
  duracion_seg: string;
  bytes: number | null;
  audiencia: "GLOBAL" | "CARGOS";
  cargos: string[];
  estado: RecursoEstado;
  obligatorio: boolean;
  orden: string;
  vigente_desde: string;
  vigente_hasta: string;
}

const FORM_VACIO: Formulario = {
  categoria_clave: "CAPACITACIONES",
  titulo: "",
  descripcion: "",
  tipo: "VIDEO",
  modoOrigen: "SUBIR",
  public_id: "",
  url_externa: "",
  duracion_seg: "",
  bytes: null,
  audiencia: "GLOBAL",
  cargos: [],
  estado: "BORRADOR",
  obligatorio: false,
  orden: "100",
  vigente_desde: "",
  vigente_hasta: "",
};

/** Deduce el proveedor a partir del enlace pegado. El backend lo revalida. */
const proveedorDeEnlace = (url: string): RecursoProveedor => {
  const u = url.toLowerCase();
  if (u.includes("youtube.com") || u.includes("youtu.be")) return "YOUTUBE";
  if (u.includes("vimeo.com")) return "VIMEO";
  return "URL";
};

const colorEstado = (estado: RecursoEstado) =>
  estado === "PUBLICADO" ? "success" : estado === "ARCHIVADO" ? "medium" : "warning";

const RecursosAdmin: React.FC = () => {
  const [recursos, setRecursos] = useState<Recurso[]>([]);
  const [categorias, setCategorias] = useState<CategoriaRecurso[]>([]);
  const [cargos, setCargos] = useState<CargoOrganizacion[]>([]);
  const [cargando, setCargando] = useState(true);
  /* null = todavía no sabemos. La autorización real la impone el backend en
     cada endpoint; esto solo evita mostrar una pantalla vacía y confusa. */
  const [autorizado, setAutorizado] = useState<boolean | null>(null);
  const [toast, setToast] = useState<Toast>({ show: false, msg: "" });

  const [form, setForm] = useState<Formulario | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [progresoSubida, setProgresoSubida] = useState(0);
  const inputArchivoRef = useRef<HTMLInputElement>(null);

  const [filtroCargo, setFiltroCargo] = useState("");
  const [aEliminar, setAEliminar] = useState<Recurso | null>(null);
  const [vistas, setVistas] = useState<{ recurso: Recurso; datos: VistaRecurso[] } | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [items, cats] = await Promise.all([adminListarRecursos(), listarCategorias()]);
      setRecursos(items);
      setCategorias(cats);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar.", color: "danger" });
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    const iniciar = async () => {
      const puede = await esAdminRecursos();
      setAutorizado(puede);
      if (puede) await cargar();
      else setCargando(false);
    };
    iniciar();
  }, [cargar]);

  /* Los cargos se piden una sola vez, al abrir el primer formulario: son ~100
     filas del DWH y no hacen falta para el listado. */
  const asegurarCargos = async () => {
    if (cargos.length > 0) return;
    try {
      setCargos(await adminListarCargos());
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudieron cargar los cargos.", color: "danger" });
    }
  };

  const abrirNuevo = async () => {
    await asegurarCargos();
    setProgresoSubida(0);
    setForm({ ...FORM_VACIO, categoria_clave: categorias[0]?.clave || "CAPACITACIONES" });
  };

  const abrirEdicion = async (recurso: Recurso) => {
    await asegurarCargos();
    setProgresoSubida(0);
    setForm({
      id: recurso.id,
      categoria_clave: recurso.categoria_clave,
      titulo: recurso.titulo,
      descripcion: recurso.descripcion || "",
      tipo: recurso.tipo,
      modoOrigen: recurso.proveedor === "CLOUDINARY" ? "SUBIR" : "ENLACE",
      public_id: recurso.proveedor === "CLOUDINARY" ? recurso.public_id || "" : "",
      url_externa: recurso.proveedor === "CLOUDINARY" ? "" : recurso.url_externa || "",
      duracion_seg: recurso.duracion_seg ? String(recurso.duracion_seg) : "",
      bytes: recurso.bytes ?? null,
      audiencia: recurso.audiencia,
      cargos: recurso.cargos ?? [],
      estado: recurso.estado,
      obligatorio: Boolean(recurso.obligatorio),
      orden: String(recurso.orden ?? 100),
      vigente_desde: recurso.vigente_desde || "",
      vigente_hasta: recurso.vigente_hasta || "",
    });
  };

  const actualizarForm = (cambios: Partial<Formulario>) =>
    setForm((previo) => (previo ? { ...previo, ...cambios } : previo));

  const alElegirArchivo = async (evento: React.ChangeEvent<HTMLInputElement>) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = ""; // permite volver a elegir el mismo archivo
    if (!archivo) return;

    setSubiendo(true);
    setProgresoSubida(0);
    try {
      const subido = await subirVideo(archivo, setProgresoSubida);
      actualizarForm({
        public_id: subido.public_id,
        duracion_seg: subido.duracion_seg ? String(subido.duracion_seg) : "",
        bytes: subido.bytes,
      });
      setToast({ show: true, msg: "Video subido correctamente.", color: "success" });
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "Falló la subida.", color: "danger" });
    } finally {
      setSubiendo(false);
    }
  };

  const guardar = async () => {
    if (!form) return;

    if (!form.titulo.trim()) {
      setToast({ show: true, msg: "Falta el título.", color: "warning" });
      return;
    }
    if (form.modoOrigen === "SUBIR" && !form.public_id) {
      setToast({ show: true, msg: "Subí el video o cambiá a la opción de enlace.", color: "warning" });
      return;
    }
    if (form.modoOrigen === "ENLACE" && !form.url_externa.trim()) {
      setToast({ show: true, msg: "Pegá el enlace del contenido.", color: "warning" });
      return;
    }
    if (form.audiencia === "CARGOS" && form.cargos.length === 0) {
      setToast({ show: true, msg: "Elegí al menos un cargo.", color: "warning" });
      return;
    }

    const proveedor: RecursoProveedor =
      form.modoOrigen === "SUBIR" ? "CLOUDINARY" : proveedorDeEnlace(form.url_externa);

    const payload: RecursoPayload = {
      categoria_clave: form.categoria_clave,
      titulo: form.titulo.trim(),
      descripcion: form.descripcion.trim() || null,
      tipo: form.tipo,
      proveedor,
      public_id: form.modoOrigen === "SUBIR" ? form.public_id : null,
      url_externa: form.modoOrigen === "ENLACE" ? form.url_externa.trim() : null,
      duracion_seg: form.duracion_seg ? Number(form.duracion_seg) : null,
      bytes: form.bytes,
      audiencia: form.audiencia,
      cargos: form.audiencia === "CARGOS" ? form.cargos : [],
      estado: form.estado,
      vigente_desde: form.vigente_desde || null,
      vigente_hasta: form.vigente_hasta || null,
      obligatorio: form.obligatorio,
      orden: Number(form.orden) || 100,
    };

    setGuardando(true);
    try {
      if (form.id) {
        await adminActualizarRecurso(form.id, payload);
      } else {
        await adminCrearRecurso(payload);
      }
      setToast({ show: true, msg: "Recurso guardado.", color: "success" });
      setForm(null);
      await cargar();
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo guardar.", color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (recurso: Recurso, estado: RecursoEstado) => {
    try {
      await adminCambiarEstado(recurso.id, estado);
      await cargar();
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cambiar el estado.", color: "danger" });
    }
  };

  const eliminar = async () => {
    if (!aEliminar) return;
    try {
      await adminEliminarRecurso(aEliminar.id);
      setToast({ show: true, msg: "Recurso eliminado.", color: "success" });
      await cargar();
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo eliminar.", color: "danger" });
    } finally {
      setAEliminar(null);
    }
  };

  const verVistas = async (recurso: Recurso) => {
    try {
      setVistas({ recurso, datos: await adminVistasRecurso(recurso.id) });
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo cargar.", color: "danger" });
    }
  };

  const cargosFiltrados = useMemo(() => {
    const texto = filtroCargo.trim().toUpperCase();
    if (!texto) return cargos;
    return cargos.filter((c) => c.cargo.includes(texto));
  }, [cargos, filtroCargo]);

  const alternarCargo = (cargo: string) => {
    setForm((previo) => {
      if (!previo) return previo;
      const yaEsta = previo.cargos.includes(cargo);
      return {
        ...previo,
        cargos: yaEsta ? previo.cargos.filter((c) => c !== cargo) : [...previo.cargos, cargo],
      };
    });
  };

  if (autorizado === false) {
    return (
      <IonPage>
        <Header titulo="Admin Recursos" />
        <IonContent>
          <div className="recurso-vacio">
            <IonText color="medium">
              <p>No tenés permisos para administrar los recursos.</p>
            </IonText>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Admin Recursos" />

      <IonContent>
        <div className="ion-padding">
          <IonButton expand="block" onClick={abrirNuevo}>
            <IonIcon slot="start" icon={addOutline} />
            Nuevo recurso
          </IonButton>
        </div>

        {cargando ? (
          <div className="recurso-vacio">
            <IonSpinner name="crescent" />
          </div>
        ) : recursos.length === 0 ? (
          <div className="recurso-vacio">
            <p>Todavía no cargaste ningún recurso.</p>
          </div>
        ) : (
          recursos.map((recurso) => (
            <IonCard key={recurso.id}>
              <IonCardContent>
                <div className="recurso-item">
                  <div className="recurso-miniatura">
                    {recurso.url_poster ? (
                      <img src={recurso.url_poster} alt="" loading="lazy" />
                    ) : (
                      <IonIcon icon={linkOutline} color="medium" size="large" />
                    )}
                    {recurso.duracion_seg ? (
                      <span className="recurso-duracion">
                        {formatearDuracion(recurso.duracion_seg)}
                      </span>
                    ) : null}
                  </div>

                  <div className="recurso-item-cuerpo">
                    <p className="recurso-titulo">{recurso.titulo}</p>
                    <IonNote>{recurso.categoria_nombre}</IonNote>

                    <div className="recurso-meta">
                      <IonBadge color={colorEstado(recurso.estado)}>{recurso.estado}</IonBadge>
                      <IonBadge color="tertiary">
                        {recurso.audiencia === "GLOBAL"
                          ? "Todos"
                          : `${recurso.cargos?.length ?? 0} cargo(s)`}
                      </IonBadge>
                      {Boolean(recurso.obligatorio) && <IonBadge color="warning">Obligatorio</IonBadge>}
                      <IonNote>
                        {recurso.vistas ?? 0} vista(s) · {recurso.completados ?? 0} completada(s)
                      </IonNote>
                    </div>

                    {recurso.audiencia === "CARGOS" && (recurso.cargos?.length ?? 0) > 0 && (
                      <div className="recurso-cargos-seleccionados">
                        {recurso.cargos!.slice(0, 4).map((c) => (
                          <IonBadge key={c} color="light">
                            {c}
                          </IonBadge>
                        ))}
                        {recurso.cargos!.length > 4 && (
                          <IonBadge color="light">+{recurso.cargos!.length - 4}</IonBadge>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="ion-margin-top">
                  <IonButton size="small" fill="outline" onClick={() => abrirEdicion(recurso)}>
                    <IonIcon slot="start" icon={createOutline} />
                    Editar
                  </IonButton>

                  {recurso.estado === "PUBLICADO" ? (
                    <IonButton
                      size="small"
                      fill="outline"
                      color="medium"
                      onClick={() => cambiarEstado(recurso, "BORRADOR")}
                    >
                      Despublicar
                    </IonButton>
                  ) : (
                    <IonButton
                      size="small"
                      fill="outline"
                      color="success"
                      onClick={() => cambiarEstado(recurso, "PUBLICADO")}
                    >
                      Publicar
                    </IonButton>
                  )}

                  <IonButton size="small" fill="outline" onClick={() => verVistas(recurso)}>
                    <IonIcon slot="start" icon={eyeOutline} />
                    Vistas
                  </IonButton>

                  <IonButton
                    size="small"
                    fill="outline"
                    color="danger"
                    onClick={() => setAEliminar(recurso)}
                  >
                    <IonIcon slot="icon-only" icon={trashOutline} />
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          ))
        )}
      </IonContent>

      {/* ------------------------- Alta / edición ------------------------- */}
      <IonModal isOpen={Boolean(form)} onDidDismiss={() => setForm(null)}>
        <IonHeader>
          <IonToolbar color="primary">
            <IonTitle>{form?.id ? "Editar recurso" : "Nuevo recurso"}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setForm(null)}>Cerrar</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent>
          {form && (
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Apartado</IonLabel>
                <IonSelect
                  value={form.categoria_clave}
                  onIonChange={(e) => actualizarForm({ categoria_clave: String(e.detail.value) })}
                >
                  {categorias.map((c) => (
                    <IonSelectOption key={c.clave} value={c.clave}>
                      {c.nombre}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Título *</IonLabel>
                <IonInput
                  value={form.titulo}
                  maxlength={200}
                  onIonChange={(e) => actualizarForm({ titulo: String(e.detail.value ?? "") })}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Descripción</IonLabel>
                <IonTextarea
                  value={form.descripcion}
                  rows={3}
                  maxlength={2000}
                  onIonChange={(e) => actualizarForm({ descripcion: String(e.detail.value ?? "") })}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Tipo</IonLabel>
                <IonSelect
                  value={form.tipo}
                  onIonChange={(e) => {
                    const tipo = e.detail.value as RecursoTipo;
                    // Documentos y enlaces nunca se suben al CDN de video.
                    actualizarForm({
                      tipo,
                      modoOrigen: tipo === "VIDEO" ? form.modoOrigen : "ENLACE",
                    });
                  }}
                >
                  <IonSelectOption value="VIDEO">Video</IonSelectOption>
                  <IonSelectOption value="DOCUMENTO">Documento</IonSelectOption>
                  <IonSelectOption value="ENLACE">Enlace</IonSelectOption>
                </IonSelect>
              </IonItem>

              {/* ----------------------- Origen ----------------------- */}
              <IonListHeader>
                <IonLabel>Contenido</IonLabel>
              </IonListHeader>

              {form.tipo === "VIDEO" && (
                <IonRadioGroup
                  value={form.modoOrigen}
                  onIonChange={(e) => actualizarForm({ modoOrigen: e.detail.value as ModoOrigen })}
                >
                  <IonItem>
                    <IonLabel>Subir un archivo de video</IonLabel>
                    <IonRadio slot="start" value="SUBIR" />
                  </IonItem>
                  <IonItem>
                    <IonLabel>Usar un enlace (YouTube, Vimeo o URL)</IonLabel>
                    <IonRadio slot="start" value="ENLACE" />
                  </IonItem>
                </IonRadioGroup>
              )}

              {form.modoOrigen === "SUBIR" ? (
                <>
                  <IonItem lines="none">
                    <IonButton
                      expand="block"
                      fill="outline"
                      disabled={subiendo}
                      onClick={() => inputArchivoRef.current?.click()}
                    >
                      <IonIcon slot="start" icon={cloudUploadOutline} />
                      {form.public_id ? "Reemplazar video" : "Elegir video"}
                    </IonButton>
                    <input
                      ref={inputArchivoRef}
                      type="file"
                      accept="video/*"
                      hidden
                      onChange={alElegirArchivo}
                    />
                  </IonItem>

                  {subiendo && (
                    <IonItem lines="none">
                      <IonLabel>
                        <p>Subiendo… {progresoSubida}%</p>
                        <IonProgressBar value={progresoSubida / 100} />
                      </IonLabel>
                    </IonItem>
                  )}

                  {form.public_id && !subiendo && (
                    <IonItem lines="none">
                      <IonNote>
                        Video cargado
                        {form.duracion_seg ? ` · ${formatearDuracion(Number(form.duracion_seg))}` : ""}
                        {form.bytes ? ` · ${(form.bytes / 1024 / 1024).toFixed(1)} MB` : ""}
                      </IonNote>
                    </IonItem>
                  )}
                </>
              ) : (
                <IonItem>
                  <IonLabel position="stacked">Enlace *</IonLabel>
                  <IonInput
                    value={form.url_externa}
                    placeholder="https://youtu.be/..."
                    onIonChange={(e) => actualizarForm({ url_externa: String(e.detail.value ?? "") })}
                  />
                </IonItem>
              )}

              {/* La duración se pide también al subir: el CDN casi siempre la
                  devuelve y queda precargada, pero si no la trae hay que poder
                  escribirla o el avance nunca llega a "completado". */}
              {form.tipo === "VIDEO" && (
                <>
                  <IonItem>
                    <IonLabel position="stacked">Duración en segundos</IonLabel>
                    <IonInput
                      type="number"
                      value={form.duracion_seg}
                      placeholder="Ej.: 420 para 7 minutos"
                      onIonChange={(e) =>
                        actualizarForm({ duracion_seg: String(e.detail.value ?? "") })
                      }
                    />
                  </IonItem>
                  <IonItem lines="none">
                    <IonNote className="ion-text-wrap">
                      {form.modoOrigen === "SUBIR"
                        ? "Se completa sola al subir el archivo. Sin duración no se puede calcular el avance."
                        : "Necesaria para calcular el avance. En YouTube y Vimeo el video corre en un reproductor externo y la app no registra el progreso."}
                    </IonNote>
                  </IonItem>
                </>
              )}

              {/* ----------------------- Audiencia ----------------------- */}
              <IonListHeader>
                <IonLabel>¿Quiénes lo ven?</IonLabel>
              </IonListHeader>

              <IonRadioGroup
                value={form.audiencia}
                onIonChange={(e) =>
                  actualizarForm({ audiencia: e.detail.value as "GLOBAL" | "CARGOS" })
                }
              >
                <IonItem>
                  <IonLabel>Toda la empresa</IonLabel>
                  <IonRadio slot="start" value="GLOBAL" />
                </IonItem>
                <IonItem>
                  <IonLabel>Solo los cargos que elija</IonLabel>
                  <IonRadio slot="start" value="CARGOS" />
                </IonItem>
              </IonRadioGroup>

              {form.audiencia === "CARGOS" && (
                <>
                  <IonItem lines="none">
                    <IonNote>{form.cargos.length} cargo(s) seleccionado(s)</IonNote>
                  </IonItem>
                  <IonSearchbar
                    value={filtroCargo}
                    placeholder="Buscar cargo"
                    onIonChange={(e) => setFiltroCargo(String(e.detail.value ?? ""))}
                  />
                  <div className="recurso-lista-cargos">
                    {cargosFiltrados.map((c) => (
                      <IonItem key={c.cargo}>
                        <IonCheckbox
                          slot="start"
                          checked={form.cargos.includes(c.cargo)}
                          onIonChange={() => alternarCargo(c.cargo)}
                        />
                        <IonLabel className="ion-text-wrap">{c.cargo}</IonLabel>
                      </IonItem>
                    ))}
                  </div>
                </>
              )}

              {/* ----------------------- Publicación ----------------------- */}
              <IonListHeader>
                <IonLabel>Publicación</IonLabel>
              </IonListHeader>

              <IonItem>
                <IonLabel position="stacked">Estado</IonLabel>
                <IonSelect
                  value={form.estado}
                  onIonChange={(e) => actualizarForm({ estado: e.detail.value as RecursoEstado })}
                >
                  <IonSelectOption value="BORRADOR">Borrador (no visible)</IonSelectOption>
                  <IonSelectOption value="PUBLICADO">Publicado</IonSelectOption>
                  <IonSelectOption value="ARCHIVADO">Archivado</IonSelectOption>
                </IonSelect>
              </IonItem>

              <IonItem>
                <IonLabel>Marcar como obligatorio</IonLabel>
                <IonToggle
                  checked={form.obligatorio}
                  onIonChange={(e) => actualizarForm({ obligatorio: e.detail.checked })}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Visible desde</IonLabel>
                <IonInput
                  type="date"
                  value={form.vigente_desde}
                  onIonChange={(e) => actualizarForm({ vigente_desde: String(e.detail.value ?? "") })}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Visible hasta</IonLabel>
                <IonInput
                  type="date"
                  value={form.vigente_hasta}
                  onIonChange={(e) => actualizarForm({ vigente_hasta: String(e.detail.value ?? "") })}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Orden en la lista</IonLabel>
                <IonInput
                  type="number"
                  value={form.orden}
                  onIonChange={(e) => actualizarForm({ orden: String(e.detail.value ?? "") })}
                />
              </IonItem>

              <div className="ion-padding">
                <IonButton expand="block" disabled={guardando || subiendo} onClick={guardar}>
                  {guardando ? <IonSpinner name="crescent" /> : "Guardar"}
                </IonButton>
              </div>
            </IonList>
          )}
        </IonContent>
      </IonModal>

      {/* ------------------------- Visualizaciones ------------------------- */}
      <IonModal isOpen={Boolean(vistas)} onDidDismiss={() => setVistas(null)}>
        <IonHeader>
          <IonToolbar color="primary">
            <IonTitle>Visualizaciones</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setVistas(null)}>Cerrar</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {vistas && (
            <>
              <IonItem lines="full">
                <IonLabel className="ion-text-wrap">
                  <h2>{vistas.recurso.titulo}</h2>
                  <p>{vistas.datos.length} persona(s) lo abrieron</p>
                </IonLabel>
              </IonItem>
              {vistas.datos.length === 0 ? (
                <div className="recurso-vacio">
                  <p>Nadie lo vio todavía.</p>
                </div>
              ) : (
                <IonList>
                  {vistas.datos.map((v) => (
                    <IonItem key={v.cedula}>
                      <IonLabel>
                        <h3>{v.cedula}</h3>
                        <p>
                          {Number(v.porcentaje_max).toFixed(0)}% ·{" "}
                          {formatearDuracion(v.segundos_vistos)} · última vez {v.fecha_ultima}
                        </p>
                      </IonLabel>
                      {Boolean(v.completado) && <IonBadge color="success">Completado</IonBadge>}
                    </IonItem>
                  ))}
                </IonList>
              )}
            </>
          )}
        </IonContent>
      </IonModal>

      <IonAlert
        isOpen={Boolean(aEliminar)}
        onDidDismiss={() => setAEliminar(null)}
        header="Eliminar recurso"
        message={`¿Eliminar "${aEliminar?.titulo ?? ""}"? Deja de verse en la app. El historial de visualizaciones se conserva.`}
        buttons={[
          { text: "Cancelar", role: "cancel" },
          { text: "Eliminar", role: "destructive", handler: eliminar },
        ]}
      />

      <IonToast
        isOpen={toast.show}
        message={toast.msg}
        color={toast.color}
        duration={3500}
        onDidDismiss={() => setToast({ show: false, msg: "" })}
      />
    </IonPage>
  );
};

export default RecursosAdmin;
